import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { getTerraReply } from "../api/gemini";

function ActionPage() {
  const navigate = useNavigate();
  const [aiResponse, setAiResponse] = useState("");
  const [activeTopic, setActiveTopic] = useState("");
  const [loading, setLoading] = useState(false);

  const handleAskAI = async (title) => {
    if (loading) return;

    // Toggle same card
    if (activeTopic === title) {
      setActiveTopic("");
      setAiResponse("");
      return;
    }

    setActiveTopic(title);
    setAiResponse("");
    setLoading(true);

    try {
      const prompt = `How can I take action to help the environment through "${title}"? Keep it short and motivating.`;
      const reply = await getTerraReply(prompt, "Environment", []);
      setAiResponse(reply);
    } catch (err) {
      setAiResponse("⚠️ Terra is taking a nap. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const cards = [
    {
      title: "Reduce Waste",
      desc: "Minimize consumption, recycle, and compost to reduce global waste.",
    },
    {
      title: "Conserve Water",
      desc: "Take shorter showers and fix leaks. Every drop counts.",
    },
    {
      title: "Sustainable Diet",
      desc: "Choose plant-based meals. Reduce your carbon footprint.",
    },
    {
      title: "Eco-Friendly Travel",
      desc: "Walk, bike, carpool or use public transport.",
    },
    {
      title: "Energy Efficiency",
      desc: "Use LEDs, unplug electronics, and insulate your home.",
    },
    {
      title: "Conscious Consumption",
      desc: "Support ethical brands and buy long-lasting goods.",
    },
  ];

  return (
    <div className="bg-white text-gray-800">
      {/* Header */}
      <section className="text-center py-20 px-4 bg-gradient-to-r from-green-100 via-white to-blue-100">
        <h1 className="text-4xl md:text-5xl font-bold mb-4">
          Your Actions, Our Planet
        </h1>
        <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
          Every small step makes a big difference. Discover actionable ways to
          contribute to environmental restoration and nurture our Earth.
        </p>
        <button
          onClick={() => navigate("/select")}
          className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-lg shadow"
        >
          Start Your Journey
        </button>
      </section>

      {/* Actions Section */}
      <section className="py-16 px-4 max-w-6xl mx-auto">
        <h2 className="text-2xl font-semibold text-center mb-10">
          Choose Your Path to Impact
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {cards.map((item, i) => (
            <div
              key={i}
              onClick={() => handleAskAI(item.title)}
              className={`cursor-pointer border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-all duration-300 ${
                activeTopic === item.title ? "bg-green-50" : "bg-white"
              }`}
            >
              <h3 className="text-lg font-semibold mb-2">{item.title}</h3>
              <p className="text-sm text-gray-600">{item.desc}</p>

              {activeTopic === item.title && (
                <div className="mt-4 text-sm p-3 rounded-lg border border-green-200 text-gray-800 bg-white/70 backdrop-blur-md">
                  {loading ? (
                    <p className="animate-pulse text-yellow-600">
                      🌿 Terra is thinking...
                    </p>
                  ) : (
                    <p>🌱 {aiResponse}</p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Impact Metrics */}
      <section className="bg-gray-50 py-12 text-center">
        <h2 className="text-xl font-semibold mb-8">Our Collective Impact</h2>
        <div className="flex flex-wrap justify-center gap-8 text-indigo-700 text-lg font-bold">
          <div>
            <span className="block text-2xl">50M+</span>
            Trees Planted
          </div>
          <div>
            <span className="block text-2xl">2.5M</span>
            Tons of Plastic Recycled
          </div>
          <div>
            <span className="block text-2xl">100%</span>
            Renewable Energy Goal
          </div>
        </div>
      </section>

      {/* Featured Resources */}
      <section className="py-16 px-4 max-w-6xl mx-auto">
        <h2 className="text-2xl font-semibold text-center mb-10">
          Featured Resources & Organizations
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {[
            {
              name: "World Wildlife Fund (WWF)",
              desc: "Leading conservation org protecting species & habitats.",
              link: "https://www.worldwildlife.org/",
            },
            {
              name: "The Nature Conservancy",
              desc: "Works globally to conserve land & water.",
              link: "https://www.nature.org/",
            },
            {
              name: "Earth Day Network",
              desc: "Promotes environmental education & advocacy.",
              link: "https://www.earthday.org/",
            },
            {
              name: "Sierra Club",
              desc: "Oldest grassroots environmental org in the U.S.",
              link: "https://www.sierraclub.org/",
            },
            {
              name: "Greenpeace",
              desc: "Promotes peace and environmental justice globally.",
              link: "https://www.greenpeace.org/",
            },
          ].map((org, i) => (
            <div
              key={i}
              className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm hover:shadow-md"
            >
              <h4 className="font-semibold mb-2">{org.name}</h4>
              <p className="text-sm text-gray-600 mb-3">{org.desc}</p>
              <a
                href={org.link}
                target="_blank"
                rel="noopener noreferrer"
                className="text-indigo-600 text-sm hover:underline"
              >
                Visit Site
              </a>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-gradient-to-r from-purple-500 to-indigo-600 text-white text-center py-12">
        <h2 className="text-2xl font-semibold mb-4">
          Ready to Make a Difference?
        </h2>
        <p className="mb-6 text-sm max-w-xl mx-auto">
          Join the TerraTalk community in fostering a healthier planet. Every
          action, big or small, matters.
        </p>
        <button
          onClick={() => navigate("/select")}
          className="bg-white text-indigo-700 px-6 py-3 rounded font-medium hover:bg-gray-100 transition"
        >
          Start Your TerraTalk Journey
        </button>
      </section>
    </div>
  );
}

export default ActionPage;
